import { GridColDef } from '@mui/x-data-grid-pro';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { CellContainer } from '@/components/tables/DataTable/components/cells';
import { entityHasAssetsIntegration } from '@/modules/assetProviderIntegrations/shared/utils';
import { BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { ROUTE_KEYS } from '@/navigation/constants';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';

/**
 * Extracts owned businesses from an entity if it has them
 */
export function extractOwnedBusinessesFromEntity(
  entity: EstatePage_EntityFragment
): EntityBusinessesTableRow[] {
  if (
    !entity.ownedOwnershipStakes ||
    entity.ownedOwnershipStakes.length === 0
  ) {
    return [];
  }

  return entity.ownedOwnershipStakes
    .filter((stake) => !!stake.ownedEntity)
    .map((stake) => ({
      id: stake.id,
      ownedEntityId: stake.ownedEntity!.id,
      name: stake.ownedEntity?.subtype?.displayName || 'Unknown Business',
      percentOwnership: `${formatPercent(stake.ownershipPercentage ?? new Decimal(0), BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE)}%`,
      netValue: formatCurrencyNoDecimals(stake.ownedValue),
      rawPercentage: stake.ownershipPercentage ?? new Decimal(0),
      rawValue: stake.ownedValue,
      isIntegrated: stake.ownedEntity
        ? entityHasAssetsIntegration(stake.ownedEntity)
        : false,
    }));
}

/**
 * Extracts integrated businesses from the list of owned businesses
 */
export function getIntegratedBusinessesFromBusinesses(
  businesses: EntityBusinessesTableRow[]
): EntityBusinessesTableRow[] {
  return businesses.filter((business) => business.isIntegrated);
}

/**
 * Extracts manually tracked businesses from the list of owned businesses
 */
export function getManuallyTrackedBusinessesFromBusinesses(
  businesses: EntityBusinessesTableRow[]
): EntityBusinessesTableRow[] {
  return businesses.filter((business) => !business.isIntegrated);
}

export interface EntityBusinessesTableRow {
  id: string;
  ownedEntityId: string;
  name: string;
  percentOwnership: string;
  netValue: string;
  rawPercentage: Decimal;
  rawValue: Decimal;
  isIntegrated: boolean;
}

export function mapBusinessesToRows(
  businesses: EntityBusinessesTableRow[]
): EntityBusinessesTableRow[] {
  return businesses.sort((a, b) => b.rawValue.minus(a.rawValue).toNumber());
}

export function useColumns(
  householdId: string
): GridColDef<EntityBusinessesTableRow>[] {
  const { navigate } = useNavigateToRoute();

  return useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
      },
      {
        field: 'percentOwnership',
        headerName: 'Percent ownership',
        flex: 1,
      },
      {
        field: 'netValue',
        headerName: 'Net value',
        flex: 1,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <CellContainer align="center">
            <IconButton
              ariaLabel="Edit business"
              icon={Edit02Icon}
              onClick={() =>
                navigate(ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS, {
                  householdId,
                  entityId: params.row.ownedEntityId,
                })
              }
              variant="transparent"
              size="xs"
            />
          </CellContainer>
        ),
      },
    ],
    [navigate, householdId]
  );
}
